import React from 'react';
import { useGetApprovedBehaviouralFlags } from 'src/hooks/user.hooks';
import { Loader } from 'ui';
import { Badge } from 'flowbite-react';
import moment from 'moment';
import { MdInfoOutline } from 'react-icons/md';
import ProfileContentHeader from './ProfileContentHeader';
import classNames from 'classnames';

interface Props {
    userId?: string;
    isSelf?: boolean;
}

interface BehavioralFlag {
    _id?: string;
    flag: string;
    comments?: string;
    level: 'user' | 'position';
    positionId?: string;
    positionTitle?: string;
    companyName?: string;
    approvedAt?: Date | string;
    approvedBy?: {
        name?: string;
        email?: string;
    };
    occurrenceCount?: number;
}

const BehavioralFlagsDisplay = ({ userId, isSelf }: Props) => {
    const { data, isLoading } = useGetApprovedBehaviouralFlags(userId);

    if (!userId || !isSelf) {
        return null;
    }

    if (isLoading) {
        return (
            <div className="py-4">
                <Loader />
            </div>
        );
    }

    const flags: BehavioralFlag[] = data?.flags || [];

    if (flags.length === 0) {
        return null;
    }

    return (
        <div className="w-full py-3">
            <ProfileContentHeader data={{ name: 'Behavioral Flags', isImp: false }} />
            <div className="mt-4 relative">
                {/* Scrollable container with max height */}
                <div
                    className={classNames(
                        'space-y-4 overflow-y-auto',
                        'max-h-[600px]', // Maximum height for scrollable area
                        'pr-2', // Padding for scrollbar
                        'scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600',
                        'scrollbar-track-transparent',
                        'hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500'
                    )}
                    style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent',
                        scrollBehavior: 'smooth',
                    }}
                >
                    {flags.map((flag, index) => (
                        <div
                            key={flag._id || index}
                            className={classNames(
                                'rounded-lg border p-4 transition-all',
                                'bg-white dark:bg-gray-800',
                                'border-gray-200 dark:border-gray-700',
                                'shadow-sm hover:shadow-md',
                                'flex-shrink-0' // Prevent cards from shrinking
                            )}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    {flag.occurrenceCount && (
                                        <div className="flex items-center gap-2 mb-2">
                                            <Badge color="gray" className="text-xs">
                                                Occurrence: {flag.occurrenceCount}
                                            </Badge>
                                        </div>
                                    )}

                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 break-words">
                                        {flag.flag}
                                    </h3>

                                    {flag.comments && (
                                        <div className="flex items-start gap-2 mb-3">
                                            <MdInfoOutline className="mt-0.5 text-gray-500 dark:text-gray-400 flex-shrink-0" size={18} />
                                            <p className="text-sm text-gray-700 dark:text-gray-300 flex-1 break-words">
                                                {flag.comments}
                                            </p>
                                        </div>
                                    )}

                                    {flag.level === 'position' && (flag.positionTitle || flag.companyName) && (
                                        <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 break-words">
                                            {flag.positionTitle && (
                                                <span>
                                                    <span className="font-medium">Position:</span> {flag.positionTitle}
                                                </span>
                                            )}
                                            {flag.positionTitle && flag.companyName && <span className="mx-2">•</span>}
                                            {flag.companyName && (
                                                <span>
                                                    <span className="font-medium">Company:</span> {flag.companyName}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {flag.approvedAt && (
                                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                                            {moment(flag.approvedAt).format('MMM DD, YYYY [at] hh:mm A')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Gradient fade indicator at bottom when scrollable - shows when there are many flags */}
                {flags.length > 3 && (
                    <>
                        <div className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none bg-gradient-to-t from-[#EFF2F5] to-transparent dark:hidden" />
                        <div className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none bg-gradient-to-t from-inherit to-transparent hidden dark:block" />
                    </>
                )}
            </div>
        </div>
    );
};

export default BehavioralFlagsDisplay;

